import { useState } from 'react';

import { Button, Checkbox, Divider, Flex, Stack, Text } from '@mantine/core';

import { CompanySubscription, DesignerSubscription, SubscriptionType } from 'models';
import { SubscriptionConfigHelpers } from 'helpers/SubscriptionConfigHelpers';
import { SubscriptionHelpers } from 'helpers/SubscriptionHelpers';

import { SubscriptionUpdateCommonProps } from '../../types';
import { PriceTag } from '../PriceTag';
import { SubscribeModalContentContainer } from '../SubscribeModalContentContainer';
import { SubscribeModalContent } from '../SubscribeModalContents';
import { useSubscriptionUpdate } from '../useSubscriptionUpdate';
import { Unsubscribe } from './Unsubscribe';

export const SubscribeForCompany = ({
    toSubscription,
    ...subscriptionProps
}: {
    toSubscription: CompanySubscription;
} & SubscriptionUpdateCommonProps) => {
    const { team } = subscriptionProps;

    const [addDesignerSubscription, setAddDesignerSubscription] = useState(false);

    const additionalSubscriptions = addDesignerSubscription
        ? [{ subscription: DesignerSubscription.STANDARD, quantity: 1 }]
        : undefined;

    const { submitSubscriptionUpdate, loading, ...updateStateAndController } = useSubscriptionUpdate({
        toSubscription,
        isDowngrade: false,
        additionalSubscriptions,
        quantity: 1,
        ...subscriptionProps,
    });

    if (toSubscription === CompanySubscription.NONE) {
        return <Unsubscribe type={SubscriptionType.COMPANY} toSubscription={toSubscription} {...subscriptionProps} />;
    }

    const { title } = SubscriptionConfigHelpers.getSubscriptionConfigByType(SubscriptionType.COMPANY)[toSubscription];
    const { title: designerTitle } = SubscriptionConfigHelpers.getSubscriptionConfigByType(SubscriptionType.DESIGNER)[
        DesignerSubscription.STANDARD
    ];

    const showUpgrades =
        SubscriptionHelpers.getSubscriptionData(SubscriptionType.DESIGNER, team.subscriptions).subscription !==
        DesignerSubscription.STANDARD;

    return (
        <SubscribeModalContentContainer title={`Sign up for ${title}`}>
            <SubscribeModalContent
                type={SubscriptionType.COMPANY}
                toSubscription={toSubscription}
                additionalSubscriptions={additionalSubscriptions}
                {...subscriptionProps}
                {...updateStateAndController}
                quantity={1}
                SubmitButton={
                    <Button size="md" onClick={submitSubscriptionUpdate} loading={loading}>
                        Sign up
                    </Button>
                }
            >
                {showUpgrades && (
                    <>
                        <Divider label="Recommended Upgrades" labelPosition="left" />
                        <Stack>
                            <Flex align="flex-start" justify="space-between">
                                <Checkbox
                                    label={
                                        <Text span>
                                            DCIDE <strong>{designerTitle}</strong> Plan
                                        </Text>
                                    }
                                    description={
                                        'Design and collaborate in real-time with peers and manufacturers ' +
                                        'on content-rich renewable energy AC & DC power system reference designs.'
                                    }
                                    checked={addDesignerSubscription}
                                    onChange={() => {
                                        setAddDesignerSubscription((current) => !current);
                                    }}
                                />

                                <PriceTag
                                    subscription={DesignerSubscription.STANDARD}
                                    billingCycle={updateStateAndController.billingCycle}
                                />
                            </Flex>
                        </Stack>
                    </>
                )}
            </SubscribeModalContent>
        </SubscribeModalContentContainer>
    );
};
